import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Checkbox, FormControlLabel, Tooltip } from '@material-ui/core';
import CustomInput from 'components/CustomInput';
import { useAddContactMutation, useUpdateContactMutation } from './vendorContactSlice';
import NotificationProvider from 'components/NotificationProvider';
import { IconLot } from 'icons';
import { usStates } from 'services/usStates';
import LoadingSpinner from 'components/LoadingSpinner';
import { vendorContactPocTypesList, vendorContactTypes } from 'features/vms/consts';
import { vendorContactStyle } from './style';
import { userHasOneClient } from 'features/user/userSlice';
import { useSelector } from 'react-redux';
import { useGetClientsQuery } from 'features/clients/clientSlice';


const UpdateContactDialog = ({ open, onClose, contact, hasPrimaryContact }) => {
    const classes = vendorContactStyle();
    const [contactToEdit, setContactToEdit] = useState({ ...contact });
    const hasOneClaim = useSelector(userHasOneClient);

    let { data: clients, error: clientError, isLoading: isLoadingClients } = useGetClientsQuery();
    const clientAccounts = clients?.flatMap(c => c.clientAccounts);

    let [updateContact, { isLoading: isUpdating }] = useUpdateContactMutation();
    let [addContact, { isLoading: isAdding }] = useAddContactMutation();

    const isLoading = isUpdating || isAdding || isLoadingClients;

    useEffect(() => {
        setContactToEdit({ ...contact });
    }, [contact, open]);

    const handleChange = (key, val) => {
        setContactToEdit({ ...contactToEdit, [key]: val });
    };

    const handleSubmit = async () => {
        let res;
        if (contactToEdit?.id) {
            res = await updateContact({ vendorContact: contactToEdit });
        } else {
            res = await addContact(contactToEdit);
        }
        if (res && !res.error) {
            NotificationProvider.success("Contact submitted successfully");
            close();
        } else {
            NotificationProvider.error("Failed to submit contact");
        }
    };

    const onChangeClientValue = (clientAccountId) => {
        let clientId = clientAccounts?.find(a => a.id === clientAccountId)?.clientId;
        let contact = { ...contactToEdit };
        contact.clientAccountId = clientAccountId;
        contact.clientId = clientId;
        setContactToEdit(contact)
    }

    const close = () => {
        setContactToEdit();
        onClose();
    }

    return (
        <>
            <Dialog open={open} onClose={close} maxWidth="sm">
                <LoadingSpinner loading={isLoading} />
                <DialogTitle>{contact?.id ? 'Update Contact' : 'Create Contact'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} >
                        {!hasOneClaim && <Grid item xs={12} sm={12}>
                            <CustomInput
                                label='Client'
                                elementType='dropdown'
                                value={contactToEdit?.clientAccountId}
                                values={clientAccounts}
                                onChange={(val) => onChangeClientValue(+val)}
                                showEmpty
                            />
                        </Grid>}
                        <Grid item xs={12} sm={6}>
                            <CustomInput
                                id="name"
                                label="Name"
                                value={contactToEdit?.name}
                                onChange={(val) => handleChange("name", val)}
                                elementType="input"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomInput
                                id="phoneNumber"
                                label="Phone"
                                value={contactToEdit?.phoneNumber}
                                onChange={(val) => handleChange("phoneNumber", val)}
                                elementType="input"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomInput
                                id="email"
                                label="Email"
                                value={contactToEdit?.email}
                                onChange={(val) => handleChange("email", val)}
                                elementType="input"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CustomInput
                                id="address"
                                label="Address"
                                value={contactToEdit?.address}
                                onChange={(val) => handleChange("address", val)}
                                icon={<IconLot />}
                                elementType="input"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CustomInput
                                id="city"
                                label="City"
                                value={contactToEdit?.city}
                                onChange={(val) => handleChange("city", val)}
                                icon={<IconLot />}
                                elementType="input"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CustomInput
                                id="state"
                                label="State"
                                value={contactToEdit?.state}
                                onChange={(val) => handleChange("state", val)}
                                values={[{ "name": "", "id": "" }, ...usStates]}
                                elementType="dropdown"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <CustomInput
                                id="zip"
                                label="Zip"
                                value={contactToEdit?.zip}
                                icon={<IconLot />}
                                onChange={(val) => handleChange("zip", val)}
                                elementType="input"
                                showEmpty
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <CustomInput
                                label='POC Type'
                                elementType='dropdown'
                                value={contactToEdit?.pocType}
                                values={vendorContactPocTypesList}
                                onChange={(val) => handleChange("pocType", +val)}
                                showEmpty
                            />
                        </Grid>

                        {/* <Tooltip title={hasPrimaryContact && contact?.type !== vendorContactTypes.PRIMARY ? "Only one primary contact is allowed" : ""}> */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    defaultChecked={contactToEdit?.type == vendorContactTypes.PRIMARY}
                                    onChange={(e, value) => handleChange('type', value ? vendorContactTypes.PRIMARY : vendorContactTypes.SECONDARY)}
                                />}
                            label="Primary"
                            className={classes.primaryContactLabel}
                        // disabled={hasPrimaryContact && contact?.type !== vendorContactTypes.PRIMARY}
                        />
                        {/* </Tooltip> */}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} color="secondary">Cancel</Button>
                    <Button disabled={isLoading} onClick={handleSubmit} color="primary" variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UpdateContactDialog;
